import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useContract } from '../../context/ContractContext';
import { useNotification } from '../../context/NotificationContext';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import BalanceCard from '../../components/dashboard/BalanceCard/BalanceCard';
import StatsCard from '../../components/dashboard/StatsCard/StatsCard';
import QuickActions from '../../components/dashboard/QuickActions/QuickActions';
import IncomeBreakdown from '../../components/dashboard/IncomeBreakdown/IncomeBreakdown';
import { formatNumber, bpsToPercent, formatUserId } from '../../utils/formatters';
import { CopyIcon } from '../../assets/icons';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isConnected, isInitializing } = useWallet();
  const { success: showSuccess } = useNotification();
  const { 
    userId, 
    isRegistered,
    isCheckingRegistration,
    getUserInfo, 
    getIncomeInfo, 
    getAvailableBreakdown,
    getDirectInfo,
    getBinaryInfo,
    getDailyBinaryStatus,
    getROIRate,
    getInvestment,
    getInvestmentCount
  } = useContract();

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [incomeData, setIncomeData] = useState(null);
  const [availableData, setAvailableData] = useState(null);
  const [directData, setDirectData] = useState(null);
  const [binaryData, setBinaryData] = useState(null);
  const [binaryStatus, setBinaryStatus] = useState(null);
  const [roiRate, setRoiRate] = useState(100);
  const [activeInvestment, setActiveInvestment] = useState(null);
  const [binaryResetTimer, setBinaryResetTimer] = useState('');

  useEffect(() => {
    // Wait for wallet initialization and registration check to complete
    if (isInitializing || isCheckingRegistration) return;
    
    if (!isConnected) {
      navigate('/');
      return;
    }
    if (!isRegistered) {
      navigate('/register');
      return;
    }
  }, [isConnected, isRegistered, isInitializing, isCheckingRegistration, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const [user, income, available, direct, binary, status, rate, invCount] = await Promise.all([
          getUserInfo(),
          getIncomeInfo(),
          getAvailableBreakdown(),
          getDirectInfo(),
          getBinaryInfo(),
          getDailyBinaryStatus(),
          getROIRate(),
          getInvestmentCount()
        ]);

        setUserData(user);
        setIncomeData(income);
        setAvailableData(available);
        setDirectData(direct);
        setBinaryData(binary);
        setBinaryStatus(status);
        setRoiRate(rate);
        setLoading(false);

        // Ceiling display: contract credits the first still-active investment
        // (falls back to the latest one once everything is capped).
        // Loaded after the main numbers are on screen, all investments in parallel.
        const invs = await Promise.all(
          Array.from({ length: invCount }, (_, i) => getInvestment(null, i))
        );
        const valid = invs.filter(Boolean);
        setActiveInvestment(valid.find((inv) => inv.active) || valid[valid.length - 1] || null);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  // Binary reset timer
  useEffect(() => {
    if (!binaryStatus?.nextResetTime) return;

    const updateTimer = () => {
      const now = Date.now();
      const resetTime = parseInt(binaryStatus.nextResetTime) * 1000;
      const diff = resetTime - now;

      if (diff <= 0) {
        setBinaryResetTimer('Resetting...');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setBinaryResetTimer(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [binaryStatus?.nextResetTime]);

  const copyToClipboard = (text, side) => {
    navigator.clipboard.writeText(text);
    showSuccess(`${side} referral link copied!`);
  };

  const getRefLink = (position) => {
    const base = window.location.origin + window.location.pathname;
    return `${base}#/register?ref=${userId}&pos=${position}`;
  };

  // Calculate ceiling percentages
  const roiCeiling = activeInvestment ? (parseFloat(activeInvestment.amount) * 2) : 0;
  const totalCeiling = activeInvestment ? parseFloat(activeInvestment.cap300) : 0;
  const roiEarned = activeInvestment ? parseFloat(activeInvestment.roiEarned) : 0;
  const totalEarned = activeInvestment ? parseFloat(activeInvestment.totalEarned) : 0;
  const roiPercent = roiCeiling > 0 ? Math.min((roiEarned / roiCeiling) * 100, 100) : 0;
  const totalPercent = totalCeiling > 0 ? Math.min((totalEarned / totalCeiling) * 100, 100) : 0;

  // Binary progress
  const binaryDailyCap = parseFloat(binaryStatus?.dailyCap || 0);
  const binaryMatchedToday = parseFloat(binaryStatus?.matchedToday || 0);
  const binaryProgress = binaryDailyCap > 0 ? Math.min((binaryMatchedToday / binaryDailyCap) * 100, 100) : 0;

  const stats = [
    {
      label: 'Total Investment',
      value: formatNumber(userData?.totalInvestment || 0),
      unit: 'USDT',
      icon: '💰',
      color: 'cyan'
    },
    {
      label: 'Total Withdrawn',
      value: formatNumber(incomeData?.totalWithdrawn || 0),
      unit: 'USDT',
      icon: '💸',
      color: 'purple'
    },
    {
      label: 'Total Income',
      value: formatNumber(incomeData?.totalIncome || 0),
      unit: 'USDT',
      icon: '📈',
      color: 'success'
    },
    {
      label: 'ROI Rate',
      value: bpsToPercent(roiRate),
      icon: '⚡',
      color: 'pink'
    }
  ];

  return (
    <PageContainer title="Dashboard" showBack={false}>
      <div className="dashboard">
        <div className="dashboard-welcome">
          <span className="welcome-text">Welcome back!</span>
          <span className="user-id">ID: {formatUserId(userId)}</span>
        </div>

        <BalanceCard 
          available={availableData?.totalAvailable || '0'}
          loading={loading}
        />

        {/* Referral Links */}
        <div className="dashboard-section">
          <h3 className="section-title">Referral Links</h3>
          <div className="referral-links-grid">
            <div className="referral-link-card left">
              <div className="ref-link-header">
                <span className="ref-link-icon">⬅️</span>
                <span className="ref-link-label">Left Link</span>
              </div>
              <div className="ref-link-url">{getRefLink('left').slice(-30)}...</div>
              <button 
                className="ref-copy-btn"
                onClick={() => copyToClipboard(getRefLink('left'), 'Left')}
              >
                <CopyIcon size={16} />
                Copy
              </button>
            </div>
            <div className="referral-link-card right">
              <div className="ref-link-header">
                <span className="ref-link-icon">➡️</span>
                <span className="ref-link-label">Right Link</span>
              </div>
              <div className="ref-link-url">{getRefLink('right').slice(-30)}...</div>
              <button 
                className="ref-copy-btn"
                onClick={() => copyToClipboard(getRefLink('right'), 'Right')}
              >
                <CopyIcon size={16} />
                Copy
              </button>
            </div>
          </div>
        </div>

        <QuickActions />

        {/* Ceiling Progress */}
        <div className="dashboard-section">
          <h3 className="section-title">Ceiling Progress</h3>
          <div className="ceiling-card">
            <div className="ceiling-item">
              <div className="ceiling-header">
                <span className="ceiling-label">ROI Ceiling (200%)</span>
                <span className="ceiling-value">{formatNumber(roiEarned)} / {formatNumber(roiCeiling)}</span>
              </div>
              <div className="ceiling-bar">
                <div className="ceiling-progress roi" style={{ width: `${roiPercent}%` }}></div>
              </div>
              <span className="ceiling-percent">{roiPercent.toFixed(1)}%</span>
            </div>
            <div className="ceiling-item">
              <div className="ceiling-header">
                <span className="ceiling-label">Total Ceiling (300%)</span>
                <span className="ceiling-value">{formatNumber(totalEarned)} / {formatNumber(totalCeiling)}</span>
              </div>
              <div className="ceiling-bar">
                <div className="ceiling-progress total" style={{ width: `${totalPercent}%` }}></div>
              </div>
              <span className="ceiling-percent">{totalPercent.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        <div className="dashboard-section">
          <h3 className="section-title">Income Breakdown</h3>
          <IncomeBreakdown 
            data={availableData} 
            incomeData={incomeData}
            loading={loading} 
          />
        </div>

        <div className="dashboard-section">
          <h3 className="section-title">Statistics</h3>
          <div className="stats-grid">
            {stats.map((stat, idx) => (
              <StatsCard key={idx} {...stat} loading={loading} />
            ))}
          </div>
        </div>

        {/* Binary Progress with Timer */}
        <div className="dashboard-section">
          <h3 className="section-title">Binary Status</h3>
          <div className="binary-progress-card">
            <div className="binary-progress-header">
              <span className="binary-progress-label">Daily Binary Limit</span>
              <span className="binary-progress-timer">
                <span className="timer-icon">⏱️</span>
                Reset in: {binaryResetTimer || '--:--:--'}
              </span>
            </div>
            <div className="binary-progress-bar">
              <div 
                className="binary-progress-fill" 
                style={{ width: `${binaryProgress}%` }}
              ></div>
            </div>
            <div className="binary-progress-info">
              <span>{formatNumber(binaryMatchedToday)} / {formatNumber(binaryDailyCap)} USDT</span>
              <span>{binaryProgress.toFixed(1)}%</span>
            </div>
          </div>

          <div className="binary-business-grid">
            <div className="binary-business-card left">
              <div className="business-header">
                <span className="business-icon">⬅️</span>
                <span className="business-title">Left Team</span>
              </div>
              <div className="business-row">
                <span className="business-label">Total Business</span>
                <span className="business-value">{formatNumber(binaryData?.leftBusinessTotal || 0, 0)}</span>
              </div>
              <div className="business-row">
                <span className="business-label">Available</span>
                <span className="business-value available">{formatNumber(binaryData?.leftBusiness || 0, 0)}</span>
              </div>
            </div>
            <div className="binary-business-card right">
              <div className="business-header">
                <span className="business-icon">➡️</span>
                <span className="business-title">Right Team</span>
              </div>
              <div className="business-row">
                <span className="business-label">Total Business</span>
                <span className="business-value">{formatNumber(binaryData?.rightBusinessTotal || 0, 0)}</span>
              </div>
              <div className="business-row">
                <span className="business-label">Available</span>
                <span className="business-value available">{formatNumber(binaryData?.rightBusiness || 0, 0)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-section">
          <h3 className="section-title">Team Stats</h3>
          <div className="team-quick-stats">
            <div className="team-stat">
              <span className="team-stat-icon">👥</span>
              <span className="team-stat-value">{directData?.directReferrals || 0}</span>
              <span className="team-stat-label">Direct Referrals</span>
            </div>
            <div className="team-stat">
              <span className="team-stat-icon">🌐</span>
              <span className="team-stat-value">{directData?.teamCount || 0}</span>
              <span className="team-stat-label">Total Team</span>
            </div>
            <div className="team-stat">
              <span className="team-stat-icon">💵</span>
              <span className="team-stat-value">{formatNumber(directData?.directBusiness || 0, 0)}</span>
              <span className="team-stat-label">Direct Business</span>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default Dashboard;